import {
  addDoc,
  collection,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore/lite";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  ViewSurvey,
  WorkshopTopbar,
  useDiscardModal,
  useIsMobile,
} from "senf-atomic-design-system";

import moment from "moment";
import { isDesktop, isMobileOnly, isTablet } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useDataPrivacyShield from "senf-atomic-design-system/src/components/special/auth/useDataPrivacyShield";
import {
  useNavigationIndex,
  usePrepareSurveyPage,
} from "senf-atomic-design-system/src/components/special/survey/hooks";
import type {
  Question,
  Result,
} from "senf-atomic-design-system/src/components/special/survey/survey.types";
import { getMostRecentlyViewedPageIndex } from "senf-atomic-design-system/src/components/special/survey/utils";
import {
  db,
  formatDateBasedOnLanguage,
  geminiRequest,
  getModeFromSessionStorage,
  getPhaseOfDateRange,
  useAuthContext,
  useCheckParticipationPhase,
  useGlobalState,
  useSchemaValidation,
} from "senf-shared";
import type { Survey as SurveyType, User } from "senf-shared/types";
import { setProject } from "../redux/actions/projectActions";
import type { ClientState } from "../redux/reducers/dataReducer";

const handleSetTimestamps = ({
  values,
  pages,
  index,
  nextIndex,
}: {
  values: Result["values"];
  pages?: Question[];
  index: number;
  nextIndex: number;
}) => {
  if (!pages) return values;

  const currentPageId = pages[index]?.uid;

  if (!currentPageId) return values;

  const currentPage = {
    ...(values[currentPageId] || {}),
    viewed: values[currentPageId]?.viewed || new Date().toISOString(),
    timeStamp: values[currentPageId]?.timeStamp || new Date().toISOString(),
  };

  if (
    !values[currentPageId]?.createdAt &&
    (pages[index].qtype === "intro" || pages[index].qtype === "demographics")
  ) {
    currentPage.createdAt = new Date().toISOString();
  }

  const modifiedValues: Result["values"] = {
    [currentPageId]: currentPage,
  };

  if (pages[nextIndex].qtype === "outro") return modifiedValues;

  const nextPageId = pages[nextIndex]?.uid;
  const nextTime = new Date(new Date().getTime() + 100).toISOString();
  const nextPage = {
    ...(values[nextPageId] || {}),
    viewed: nextTime,
  };

  if (!values[nextPageId]?.createdAt) {
    nextPage.createdAt = nextTime;
  }

  modifiedValues[nextPageId] = nextPage;

  return modifiedValues;
};

function Survey({
  repeatable = false,
  // variant = "fullscreen",
}) {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { displayDiscardModal } = useDiscardModal();
  const [survey, setSurvey] = useState<SurveyType>();
  const project = useSelector((state: ClientState) => state.data.project);
  const [index, setIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [entryId, setEntryId] = useState<string>();
  const [loadingUserEntries, setLoadingUserEntries] = useState(true);
  const searchParams = new URLSearchParams(window.location.search);
  const surveyMode = searchParams.get("surveyMode");
  const testMode = surveyMode === "test" || surveyMode === "preview";
  const { user, setUser, getUserData, signIn, submitEditDetails } =
    useAuthContext();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [surveyLoginFormShown, setSurveyLoginFormShown] = useState(false);
  const getParticipationPhase = useCheckParticipationPhase();
  const { handleDataPrivacy } = useDataPrivacyShield(project);
  const urlParams = new URLSearchParams(window.location.search);
  const utm_id = urlParams.get("utm_id");

  const organizationId = project?.organizationId;

  const { projectId, surveyId } = useParams();

  useEffect(() => {
    if (projectId) {
      dispatch(setProject(projectId, navigate));
    }
  }, [projectId]);

  const formik = useFormik<Result["values"]>({
    initialValues: {},
    onSubmit: () => {},
  });

  const { currentPage, currentPageRole } = usePrepareSurveyPage({
    pages: survey?.pages || [],
    index,
  });
  const { nextIndex, isLastPage } = useNavigationIndex({
    pages: survey?.pages || [],
    index,
    formik,
  });

  const updateUserData = () => {
    if (user?.userId) getUserData(user.userId, project).then(setUser);
  };

  const docRef = doc(
    db,
    `organizations/${organizationId}/projects/${projectId}/surveys/${surveyId}`,
  );

  useEffect(() => {
    async function fetchData() {
      try {
        const surveyDoc = await getDoc(docRef);
        if (!surveyDoc.exists()) return;
        const survey = surveyDoc.data() as SurveyType;
        if (!survey) return;

        survey.formattedStartDate = formatDateBasedOnLanguage(
          new Date(survey?.startDate),
          i18n.resolvedLanguage,
        );
        survey.formattedEndDate = formatDateBasedOnLanguage(
          new Date(survey?.endDate),
          i18n.resolvedLanguage,
        );

        survey.phase = getPhaseOfDateRange(survey?.startDate, survey?.endDate);

        setSurvey(survey);
      } catch (error) {
        console.error(error);
      }
    }
    fetchData();
  }, [organizationId, projectId, surveyId, project, i18n.resolvedLanguage]);

  const entriesColRef = collection(docRef, "entries");

  const { surveySignIn, addUserDetails } = useSchemaValidation();

  const loginFormik = useFormik<
    Partial<{ email: string; password: string; confirmPassword: string }>
  >({
    initialValues: {
      handle: "",
      email: "",
      password: "",
      confirmPassword: "",
      newsletter: false,
      providerId: "",
      referrer: localStorage.getItem("referrer") || "",
      projectId: projectId || localStorage.getItem("projectId"),
      utm_id: utm_id || localStorage.getItem("utm_id") || "",
      mode: testMode ? "test" : "",
    },
    validationSchema: surveySignIn,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: () => {
      console.log("submit");
    },
  });

  const demographicsFormik = useFormik<
    Partial<User & { email: string; password: string }>
  >({
    initialValues: {
      handle: "Gastnutzer",
      userId: "",
      postcode: "",
      age: "",
      gender: "",
      newsletter: false,
      providerId: "anonymous",
      referrer: localStorage.getItem("referrer") || "",
      projectId: projectId || localStorage.getItem("projectId"),
      visitorId: localStorage.getItem("visitorId") || null,
      utm_id: utm_id || localStorage.getItem("utm_id") || "",
      mode: testMode ? "test" : "",
    },
    validationSchema: addUserDetails(project, translation),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: () => {
      console.log("submit");
    },
  });

  useEffect(() => {
    demographicsFormik.setValues({
      ...demographicsFormik.values,
      ...Object.fromEntries(
        Object.entries(user || {}).map(([key, value]) => [key, value || ""]),
      ),
    });
    if (user?.isAnonymous)
      demographicsFormik.setFieldValue("providerId", "anonymous");
    if (!user?.handle)
      demographicsFormik.setFieldValue(
        "handle",
        project?.settings?.guestLogin ? "Gastnutzer" : "Temporärer Nutzer",
      );
  }, [user]);

  useEffect(() => {
    if (entryId || !survey?.pages || !surveyId) {
      return;
    }

    const entry = ((user?.surveyEntries || []) as Result[])?.find(
      (entry) => entry?.surveyMode !== "test" && entry.surveyId === surveyId,
    );

    if (!user?.surveyEntries?.length || repeatable || !entry) {
      setLoadingUserEntries(false);
      return;
    }

    setEntryId(entry.entryId);
    if (!entry.values) return;
    // show last page if completed
    if (entry.completed && !testMode) {
      const outroIndex = survey?.pages.findIndex((e) => e.qtype === "outro");
      setIndex(outroIndex);
      return;
    }
    if (!testMode) formik.setValues(entry.values);

    // const index = Object.keys(entry.values).length + assureCorrectIndex;
    const mostRecentIndex = getMostRecentlyViewedPageIndex(
      survey?.pages,
      entry.values,
      index,
    );
    // show last page that was viewed but not completed
    if (mostRecentIndex && !testMode) setIndex(mostRecentIndex);
    setLoadingUserEntries(false);
  }, [survey?.pages, user, user?.surveyEntries, surveyId]);

  if ((survey?.pages?.length || 0) < 0) return null;
  if (!project) return null;

  const handleTranslate = async (value) => {
    try {
      return await geminiRequest({
        body: `Translate this text: ${value} into ${i18n.resolvedLanguage}`,
        lang: project?.settings?.originalLanguage,
        assistantId: "default",
      });
    } catch (error) {
      console.error("Error fetching translation:", error);
      throw error; // Rethrow to allow caller to handle it as well
    }
  };

  const handleTranslateIfNecessary = async (
    values: Result["values"],
    pages: Question[],
    currentPageId: string,
  ) => {
    const currentPageData = values[currentPageId];

    if (
      pages[index].qtype === "text" ||
      pages[index]?.mapbasedQtype === "textarea" ||
      pages[index]?.textResponse ||
      pages[index]?.otherOption ||
      pages[index]?.mapbasedOtherOption
    ) {
      if (
        !project?.settings?.originalLanguage ||
        project?.settings?.originalLanguage === i18n.resolvedLanguage
      )
        return currentPageData;

      if (pages[index].qtype === "text") {
        const updateData = currentPageData;
        const entries = Object.entries(currentPageData?.value || {});
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const translatedValue = await handleTranslate(value?.value);
            const newValue = { ...value };
            if (!translatedValue) return [key, newValue];
            newValue.translations = {
              [project?.settings?.originalLanguage || "de"]: translatedValue,
            };
            return [key, newValue];
          }),
        );

        updateData.value = Object.fromEntries(translatedEntries);
        return updateData;
      }

      // Translate mapbased textareas
      if (
        (pages[index]?.mapbasedQtype === "textarea" ||
          pages[index]?.mapbasedOtherOption) &&
        currentPageData?.geoData
      ) {
        let geoData;
        try {
          geoData = JSON.parse(currentPageData?.geoData);
        } catch (error) {
          console.error(error); // Proper error logging
        }

        if (geoData?.features) {
          const propertyKey = pages[index]?.mapbasedOtherOption
            ? "comment"
            : "description";

          // Use Promise.all to wait for all translations to complete
          const updatedFeatures = await Promise.all(
            geoData.features.map(async (feature) => {
              const value = feature.properties?.[propertyKey];

              if (!value) return feature; // Return the feature unchanged if there's no value

              const translatedValue = await handleTranslate(value);
              feature.properties.translations = {
                [project?.settings?.originalLanguage || "de"]: translatedValue,
              };
              return feature;
            }),
          );

          // Create the new geoData object with updated features
          const updateData = {
            ...currentPageData,
            geoData: JSON.stringify({
              ...geoData,
              features: updatedFeatures,
            }),
          };

          return updateData;
        }
      }

      // Rating, Stars, Slider & other option
      if (pages[index]?.textResponse || pages[index]?.otherOption) {
        const value = currentPageData?.comment;

        if (!value) return currentPageData;

        const translatedValue = await handleTranslate(value);
        if (!translatedValue) return currentPageData;

        const updateData = currentPageData;
        updateData.translations = {
          [project?.settings?.originalLanguage || "de"]: translatedValue,
        };
        return updateData;
      }
    }
    return currentPageData;
  };

  const handleStartSurvey = async () => {
    const {
      userId = null,
      age: year,
      gender = null,
      postcode = null,
      utm_id,
    } = demographicsFormik.values;

    const age =
      year !== "" || year !== null
        ? moment()?.diff(moment(year, "YYYY"), "years") || null
        : null;
    const device = isMobileOnly
      ? "mobile"
      : isTablet
        ? "tablet"
        : isDesktop
          ? "desktop"
          : "other";

    const updatedValues = { ...formik.values };

    // timestamps
    Object.assign(
      updatedValues,
      handleSetTimestamps({
        values: updatedValues,
        pages: survey?.pages,
        index,
        nextIndex,
      }),
    );

    const surveyEntry = {
      values: updatedValues,
      surveyId,
      organizationId,
      projectId,
      createdAt: new Date().toISOString(),
      userId,
      age,
      gender,
      userPostcode: postcode,
      device,
    };
    if (utm_id) {
      surveyEntry.utm_id = utm_id;
    }
    if (testMode) {
      surveyEntry.surveyMode = "test";
    }

    await addDoc(entriesColRef, surveyEntry)
      .then(async (entryDoc) => {
        await updateDoc(
          doc(
            db,
            `organizations/${organizationId}/projects/${projectId}/surveys/${surveyId}/entries/${entryDoc.id}`,
          ),
          {
            entryId: entryDoc.id,
          },
        );
        setEntryId(entryDoc.id);
        setSubmitting(false);
      })
      .catch((error) => {
        console.log("error", error);

        setSubmitting(false);
      });
  };

  const handleSaveNext = async ({
    values: val,
    completed,
  }: { values?: Result["values"]; completed?: boolean } = {}) => {
    if (!entryId) return;

    const values = val || formik.values;
    // demographics
    const {
      age: year,
      userId = "",
      gender = "",
      postcode = "",
    } = demographicsFormik.values;
    const age =
      year !== "" || year !== null
        ? moment()?.diff(moment(year, "YYYY"), "years") || null
        : null;

    const currentPageId = survey?.pages?.[index]?.uid;
    const nextPageId = survey?.pages?.[nextIndex]?.uid;
    const updatedValues = { ...values };
    if (currentPageId) {
      // translations
      Object.assign(updatedValues, {
        [currentPageId]: await handleTranslateIfNecessary(
          values,
          survey?.pages,
          currentPageId,
        ),
      });

      // timestamps
      Object.assign(
        updatedValues,
        handleSetTimestamps({
          values: updatedValues,
          pages: survey?.pages,
          index,
          nextIndex,
        }),
      );

      if (currentPageId && updatedValues[currentPageId]) {
        await formik.setFieldValue(currentPageId, updatedValues[currentPageId]);
      }
      if (nextPageId && updatedValues[nextPageId]) {
        await formik.setFieldValue(nextPageId, updatedValues[nextPageId]);
      }
    }

    const surveyEntry: Partial<Result> = {
      values: updatedValues,
      age,
      gender,
      userId,
      userPostcode: postcode,
      completed: completed ? new Date().toISOString() : false,
    };

    if (
      project?.settings?.originalLanguage &&
      project?.settings?.originalLanguage !== i18n.resolvedLanguage
    ) {
      surveyEntry.lang = i18n.resolvedLanguage;
    } else {
      delete surveyEntry.lang;
    }

    await updateDoc(
      doc(
        db,
        `organizations/${organizationId}/projects/${projectId}/surveys/${surveyId}/entries/${entryId}`,
      ),
      surveyEntry,
    )
      .then(() => {
        setSubmitting(false);
      })
      .catch((error) => {
        console.log("error", error);
        setSubmitting(false);
      });
  };

  const handleClose = () => {
    if (currentPageRole !== "outro") {
      displayDiscardModal({
        title: `${t("util:exit", { val: t("global:survey") })}?`,
        description: t("survey:discardDescription"),
        submitText: t("util:exit", { val: t("global:survey") }),

        onSubmit: async () => {
          updateUserData();
          navigate(`${pathname.split("/").slice(0, -2).join("/")}/info`);
        },
      });
    } else {
      updateUserData();
      navigate(`${pathname.split("/").slice(0, -2).join("/")}/info`);
    }
  };

  const handleProcessNext = async (u = user) => {
    setSubmitting(true);
    if (currentPageRole === "intro") {
      const userId = u?.userId || u?.uid;
      if (!repeatable && !userId) {
        const privacyAccepted = await handleDataPrivacy();

        if (!privacyAccepted) {
          setSubmitting(false);
          return;
        }

        if (project?.settings?.guestLogin) {
          await signIn({
            formikStore: demographicsFormik,
            provider: "anonymous",
            id: Math.random().toString(36).substring(2, 15),
            project,
          });
        } else {
          setSurveyLoginFormShown(true);
          setSubmitting(false);
          return new Promise((resolve) => {
            resolve(false);
          });
        }
      }
      if (u?.userId) {
        // setSurveyLoginFormShown(false);
      }
    }

    return new Promise(async (resolve) => {
      updateUserData();
      // IF NOT LOGGED IN STOP HERE

      // IF USER DEMOGRAPHICS NOT ADDED
      // if (currentPageRole === 'intro' && !repeatable && u?.userId && u.isAnonymous) {
      //   setSurveyLoginFormShown(true);
      //   setSubmitting(false);
      //   resolve(false);
      //   if (!u?.profileCompleted) {
      //     return;
      //   }
      // }

      // IF ON FIRST PAGE
      if (currentPageRole === "intro" && !entryId && !loadingUserEntries) {
        handleStartSurvey()
          .then(() => resolve(true))
          .catch(() => {
            setSubmitting(false);
            resolve(false);
          });
        return;
      }
      if (currentPageRole === "demographics") {
        submitEditDetails({ formikStore: demographicsFormik, project })
          .then(() =>
            handleSaveNext({ completed: isLastPage }).then(() => resolve(true)),
          )
          .catch(console.warn)
          .finally(() => setSubmitting(false));
        return;
      }

      if (entryId) {
        // if (survey?.pages[index + 1]?.qtype === "demographics" && u?.profileCompleted) {
        //   console.log('skipping')
        //   setIndex(e => e + 2)
        //   return
        // }
        handleSaveNext({
          completed: isLastPage,
        })
          .then(() => resolve(true))
          .catch((error) => {
            console.log(error);
            setSubmitting(false);
            resolve(false);
          });

        return;
      }
      setSubmitting(false);
      resolve(false);
    });
  };

  const outroButtons = [
    (project?.brief || project?.title) &&
      currentPage?.qbuttonInfoPage !== false && {
        buttonLabel: t("global:toInfoPage"),
        onClick: () => navigate(`/projects/${project.projectId}/info`),
      },
    ...(project?.ideaMaps.length > 0
      ? project.ideaMaps.flatMap((ideaMap) => {
          const active =
            getPhaseOfDateRange(ideaMap.startDate, ideaMap.endDate) === "while";
          if (
            ideaMap?.settings?.publicAccess === false ||
            !active ||
            currentPage?.qbuttonIdeaMaps === false
          )
            return [];

          return [
            {
              type: "addPost",
              buttonLabel: t("survey:successPage.addPost"),
              ideaMapTitle: translation?.[ideaMap?.title] || ideaMap?.title,
              onClick: () =>
                navigate(
                  `/projects/${project.projectId}/ideaMaps/${ideaMap.ideaMapId}/addIdea`,
                ),
            },
          ];
        })
      : []),
  ].filter(Boolean);

  return (
    <>
      {/* {ReactDOM.createPortal(
      <div className="fixed left-0 top-0 z-[999999] m-5 flex gap-2 text-sm font-bold text-white">
        <div className="rounded bg-black p-2 shadow">
          {project?.settings?.guestLogin
            ? "Guest login enabled"
            : "Guest login disabled"}
        </div>
        <div className="rounded bg-black p-2 shadow">
          User: {user?.email || user?.handle || user?.userId}, ZIP:{" "}
          {user?.postcode}, Gender: {user?.gender}, YoB: {user?.age}
        </div>
      </div>,
      document.body
    )} */}
      {getModeFromSessionStorage() === "workshop" &&
        user?.userId &&
        !isMobile && <WorkshopTopbar />}

      <ViewSurvey
        survey={survey}
        formik={formik}
        loginFormik={loginFormik}
        demographicsFormik={demographicsFormik}
        surveyId={surveyId}
        project={project}
        outroButtons={outroButtons}
        handleClose={handleClose}
        submitting={submitting}
        setSubmitting={setSubmitting}
        phase={testMode ? "while" : survey?.phase}
        handleProcessNext={handleProcessNext}
        handleSaveNext={handleSaveNext}
        setIndex={setIndex}
        index={index}
        surveyMode={surveyMode}
        surveyLoginFormShown={surveyLoginFormShown}
        setSurveyLoginFormShown={(e) => {
          setSurveyLoginFormShown(e);
        }}
        user={user}
      />
    </>
  );
}

export default Survey;
